// src/app/pages/components/NetworkPulseOverlay.tsx
import { useEffect, useState } from 'react';

/**
 * Purely decorative "data flowing through the network" effect for the
 * login hero illustration. Pure SVG + SMIL/CSS — no canvas, no WebGL,
 * no animation library — so it adds effectively nothing to bundle size
 * or runtime cost, and never touches layout (see integration notes).
 *
 * IMPORTANT: the viewBox below (0 0 1305 648) matches the *actual*
 * pixel dimensions of login-hero.webp/png. Every `d` path below was
 * traced directly from the red guide-line markup supplied for this
 * artwork: the red pixels were isolated, skeletonized to a 1px-wide
 * centerline, and walked from each device's connector to the central
 * hub, then simplified to a handful of cubic-bezier control points
 * and verified by re-rendering the beziers back over the original
 * artwork to confirm pixel alignment. So a point at (x, y) here lands
 * on the same pixel in the artwork regardless of how large the hero
 * is rendered on screen — as long as this SVG and the <img> share the
 * same box, which .login-illustration__frame guarantees.
 *
 * Each path runs from a device's own visible wire terminal to the
 * central hub glow at the base of the monitor stand (~615, 428),
 * following the exact marked route the whole way — including the
 * bundled final stretch where the server-stack and flag-device wires
 * run alongside each other right before the hub, and the two paths
 * that hug the monitor bezel (cloud along the left/bottom edge, AI
 * Automation along the right/bottom edge). If a packet ever looks
 * even slightly off a wire at some viewport size, nudge the relevant
 * cubic control points below — every coordinate here is real
 * image-space geometry lifted from the guide lines, not a guess.
 */

interface NetworkLink {
  id: string;
  /** SVG path `d`, hand-traced onto the real illustrated wire. */
  d: string;
  /** Base duration for a packet's full travel, in seconds. */
  duration: number;
  /** Per-particle stagger so packets don't all launch in sync. */
  delays: number[];
  /** Some links carry packets center→device, others device→center. */
  reverse?: boolean;
  color: 'violet' | 'cyan';
}

// Central hub: the light-burst at the base of the monitor stand, (615, 428).
const NETWORK_LINKS: NetworkLink[] = [
  {
    // Cloud (top-left) — traced from the red guide line: up from the
    // cloud's platform glow, along the monitor's left bezel, a short
    // jog near the bezel corner, then along the bottom edge into the hub.
    id: 'hub-cloud',
    d: 'M 262 254 C 268.5 255.5 294.7 250.8 301 263 C 307.3 275.2 297.8 315 300 327 C 302.2 339 321.8 326.7 314 335 C 306.2 343.3 262.7 369.2 253 377 C 243.3 384.8 239 375.2 256 382 C 273 388.8 330 406.5 355 418 C 380 429.5 373.7 450.2 406 451 C 438.3 451.8 520.7 427.3 549 423 C 577.3 418.7 571.5 424.7 576 425',
    duration: 7.5,
    delays: [0, 4.1],
    color: 'violet',
  },
  {
    // AI Automation card (top-right) — traced from the red guide line:
    // drops from the card's bottom-left terminal, along the monitor's
    // right bezel, down that edge, then in to the hub.
    id: 'hub-ai-automation',
    d: 'M 881 284 C 882.8 283.7 889.7 281.5 892 282 C 894.3 282.5 896.3 277 895 287 C 893.7 297 888.7 328.8 884 342 C 879.3 355.2 880.5 358.7 867 366 C 853.5 373.3 836.5 380.3 803 386 C 769.5 391.7 691.2 395.5 666 400 C 640.8 404.5 654.3 410.8 652 413',
    duration: 8.2,
    delays: [1.6],
    color: 'cyan',
    reverse: true,
  },
  {
    // Stacked database (bottom-left) — traced from the red guide line.
    id: 'hub-database',
    d: 'M 400 548 C 422.8 541.7 507.7 530.5 537 510 C 566.3 489.5 569.5 439.2 576 425',
    duration: 9,
    delays: [2.8, 6.5],
    color: 'violet',
    reverse: true,
  },
  {
    // Server / box stack (right) — traced from the red guide line. Runs
    // alongside the flag-device wire for the final stretch into the
    // hub, matching how the two lines were bundled together on the guide.
    id: 'hub-server-stack',
    d: 'M 1021 459 C 1011.3 463.2 991.3 475.8 963 484 C 934.7 492.2 870.8 506 851 508 C 831.2 510 850 498.7 844 496 C 838 493.3 827.7 497.7 815 492 C 802.3 486.3 788.2 472.3 768 462 C 747.8 451.7 711.7 436.5 694 430 C 676.3 423.5 669 425.8 662 423 C 655 420.2 653.7 414.7 652 413',
    duration: 7.8,
    delays: [0.9],
    color: 'violet',
  },
  {
    // Flag/folder device (bottom-center) — traced from the red guide
    // line. Bundles alongside the server-stack wire for the final
    // stretch into the hub, matching the guide exactly.
    id: 'hub-flag-device',
    d: 'M 837 520 C 839.2 518 848.8 512 850 508 C 851.2 504 849.8 498.7 844 496 C 838.2 493.3 827.7 497.7 815 492 C 802.3 486.3 788.2 472.3 768 462 C 747.8 451.7 711.7 436.5 694 430 C 676.3 423.5 669 425.8 662 423 C 655 420.2 653.7 414.7 652 413',
    duration: 6.6,
    delays: [3.6],
    color: 'cyan',
  },
];

const COLORS = {
  violet: '#8B5CF6',
  cyan: '#22D3EE',
};

// Set to true only for local visual QA: renders each traced path as a
// visible stroke so it can be checked against the artwork directly in
// the browser. Verified against the source guide lines already —
// leave this false for production; it adds a visible debug overlay.
const DEBUG_SHOW_GUIDE_PATHS = false;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);

    const handleChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return reduced;
}

export function NetworkPulseOverlay() {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <svg
      className="network-pulse-overlay"
      viewBox="0 0 1305 648"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id="np-glow" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* One invisible motion path per link — the packet's <g> rides
            these via <mpath>, so the path only needs to exist once. */}
        {NETWORK_LINKS.map((link) => (
          <path key={link.id} id={`np-path-${link.id}`} d={link.d} fill="none" />
        ))}

        {/* Comet trail: transparent at the tail (bbox x=0 → local
            x=-38, behind the head) fading up to full color at the
            head (bbox x=1 → local x=0). objectBoundingBox units mean
            this stays correct under animateMotion's transform without
            any userSpace math. */}
        <linearGradient id="np-trail-violet" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={COLORS.violet} stopOpacity="0" />
          <stop offset="100%" stopColor={COLORS.violet} stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="np-trail-cyan" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={COLORS.cyan} stopOpacity="0" />
          <stop offset="100%" stopColor={COLORS.cyan} stopOpacity="0.85" />
        </linearGradient>
      </defs>

      {DEBUG_SHOW_GUIDE_PATHS &&
        NETWORK_LINKS.map((link) => (
          <path
            key={`debug-${link.id}`}
            d={link.d}
            fill="none"
            stroke={link.color === 'cyan' ? '#22D3EE' : '#8B5CF6'}
            strokeWidth="2"
            strokeDasharray="6 4"
            opacity="0.9"
          />
        ))}

      {/* Soft breathing halo layered on top of the artwork's own static
          glow at the hub — subtle enough not to double up on it. */}
      <circle cx="615" cy="428" r="26" fill={COLORS.violet} opacity="0.14" filter="url(#np-glow)">
        {!prefersReducedMotion && (
          <animate
            attributeName="r"
            values="26;36;26"
            dur="4.5s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
          />
        )}
      </circle>

      {!prefersReducedMotion &&
        NETWORK_LINKS.flatMap((link) =>
          link.delays.map((delay, index) => {
            const particleId = `${link.id}-${index}`;
            const trailGradient = `url(#np-trail-${link.color})`;
            const headColor = COLORS[link.color];

            return (
              <g key={particleId} opacity="0">
                {/* Trail: drawn behind the head in local (pre-rotation)
                    space; rotate="auto" on the motion keeps local +x
                    aligned with the direction of travel, so this stays
                    behind the packet on both forward and reverse links. */}
                <line
                  x1="-38"
                  y1="0"
                  x2="0"
                  y2="0"
                  stroke={trailGradient}
                  strokeWidth="4.5"
                  strokeLinecap="round"
                />
                {/* Bright leading point */}
                <circle cx="0" cy="0" r="3.4" fill="#ffffff" filter="url(#np-glow)" />
                <circle cx="0" cy="0" r="2" fill={headColor} />

                <animateMotion
                  dur={`${link.duration}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                  rotate="auto"
                  keyPoints={link.reverse ? '1;0' : '0;1'}
                  keyTimes="0;1"
                  calcMode="linear"
                >
                  <mpath href={`#np-path-${link.id}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.08;0.85;1"
                  dur={`${link.duration}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
              </g>
            );
          }),
        )}
    </svg>
  );
}
